import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { Link } from "react-router-dom";

import { client } from "../../api";
import { AppModel } from "../../app/app-model";
import { ErrorPage } from "../../app/error-page";
import {
  type Column,
  DataTable,
} from "../../components/ui-kit/tables/data-table";
import { showToast } from "../../components/ui-kit/toasts";
import { DataModel } from "../../models/data-model";
import { useAbortableFetch } from "../../utils/use-abortable-fetch";
import { ExtractionModel } from "../patient/documents/extraction-model";
import { ExtractionPanelLazy } from "../patient/documents/extraction-panel-lazy";
import {
  ExtractionCell,
  ReportWithExtraction,
} from "../patient/documents/extraction-status";
import { reportTypes } from "../patient/reports/enums";

// Documents worth a reviewer's attention: extracted and awaiting sign-off, or
// failed and needing a retry. Ingested ones are done, pending/processing ones
// are not actionable yet.
const QUEUE_STATUSES = ["ready", "failed"];

interface QueueResponse {
  medical_reports: ReportWithExtraction[];
}

/**
 * Everything waiting for extraction review, across patients.
 *
 * A cardiologist's point: correcting an extraction during the consultation is
 * too slow, and it is better if one person checks documents in bulk afterwards.
 * This is that work list — the same review panel as the patient's Reports tab,
 * reached from one place instead of by opening patients one at a time.
 */
export const ExtractionQueueRoute = observer(function ExtractionQueueRoute({
  app,
}: {
  app: AppModel;
}) {
  const { t } = useLingui();
  const [model] = useState(() => {
    const m = new DataModel<ReportWithExtraction>(
      [],
      app.seerlinqApi.permissions,
      app.seerlinqApi.requireUserUUID(),
    );
    m.idAttr = "uuid";
    return m;
  });
  const [extractionModel] = useState(() => new ExtractionModel());
  const [selected, setSelected] = useState<ReportWithExtraction | null>(null);

  const { loading, error, refetch } = useAbortableFetch(
    (signal) =>
      // TODO(#629): raw client, like the rest of this feature.
      client.get<QueueResponse>({
        url: "/api/v1/medical-reports",
        query: { extraction_statuses: QUEUE_STATUSES },
        signal,
      }),
    (response) => model.updateData(response.data?.medical_reports ?? []),
    [],
  );

  async function openReview(report: ReportWithExtraction) {
    setSelected(report);
    await extractionModel.load(report);
  }

  async function retry(report: ReportWithExtraction) {
    await extractionModel.retry(report.uuid);
    // The re-run document is pending now, so the reload drops it from the list.
    showToast({
      message: t`Extraction queued. The document comes back to this list once it is ready.`,
      variant: "info",
    });
    await refetch();
  }

  const columns: Column<ReportWithExtraction>[] = [
    {
      header: <Trans>Patient</Trans>,
      attr: "patient_id",
      sticky: true,
      width: 100,
      filter: {
        kind: "text",
        group: t`Identity`,
        placeholder: t`Search…`,
      },
      cell: ({ item }) => (
        <strong>
          <Link to={`/patient/${item.patient_id}/reports`}>
            {item.patient_id}
          </Link>
        </strong>
      ),
    },
    {
      header: <Trans>Date</Trans>,
      attr: "report_date",
      type: "date",
      sort: true,
      initSort: "desc",
      width: 110,
    },
    {
      header: <Trans>Type</Trans>,
      attr: "report_type",
      type: "enum",
      values: reportTypes,
      filter: {
        kind: "multi",
        group: t`Report`,
        options: new Map(Object.entries(reportTypes)),
      },
    },
    {
      header: <Trans>Filename</Trans>,
      attr: "original_name",
      type: "string",
      multiline: 3,
    },
    {
      header: <Trans>Extraction</Trans>,
      width: 170,
      cell: ({ item }) => (
        <ExtractionCell
          report={item}
          onReview={(report) => void openReview(report)}
          onRetry={(report) => void retry(report)}
        />
      ),
    },
  ];

  if (error)
    return (
      <ErrorPage
        title={t`Error loading extraction queue`}
        message={error instanceof Error ? error.message : t`Unexpected error`}
      />
    );

  return (
    <>
      <DataTable
        persistState
        readOnly
        isLoading={loading}
        model={model}
        itemKeyField="uuid"
        filterNoun={(count) =>
          t`${plural(count, { one: "document", other: "documents" })}`
        }
        columns={columns}
      />

      {selected && (
        <ExtractionPanelLazy
          key={selected.uuid}
          app={app}
          report={selected}
          model={extractionModel}
          // A decided document leaves the queue.
          onDecided={() => void refetch()}
          // The queue never polls: a re-run left running drops out of it, and
          // one that failed comes back as failed.
          onRerun={() => void refetch()}
          // Same as the reports tab: the open panel reads the row it was
          // given, so patch it as well as reloading the queue.
          onReportUpdated={(reportDate) => {
            setSelected((prev) => prev && { ...prev, report_date: reportDate });
            void refetch();
          }}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
});
